import { PropsWithChildren } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";

import styles from "./toast.module.scss";

type ToastVariant = "error" | "success";

type ToastIconProps = {
  variant: ToastVariant;
};

const ToastIcon = ({ variant }: ToastIconProps) => {
  const variantToIconMap: Record<ToastVariant, IconDefinition> = {
    error: faCircleExclamation,
    success: faCircleCheck,
  };

  const iconForVariant = variantToIconMap[variant] ?? faCircleInfo;

  return (
    <div className={styles.iconContainer}>
      <FontAwesomeIcon icon={iconForVariant} />
    </div>
  );
};

type ToastCloseButtonProps = {
  onClick: () => void;
};

const ToastCloseButton = ({ onClick }: ToastCloseButtonProps) => (
  <button
    onClick={onClick}
    type="button"
    aria-label="Close message"
    className={styles.iconContainer}
  >
    <FontAwesomeIcon icon={faXmark} />
  </button>
);

type ToastProps = {
  variant: ToastVariant;
  onClose?: () => void;
  className?: string;
};

export const Toast = ({
  variant,
  onClose,
  className,
  children,
}: PropsWithChildren<ToastProps>) => (
  <div
    className={classNames(
      styles.toast,
      {
        [styles.variantError]: variant === "error",
        [styles.variantSuccess]: variant === "success",
      },
      className,
    )}
  >
    <ToastIcon variant={variant} />
    <div>{children}</div>
    {onClose && <ToastCloseButton onClick={onClose} />}
  </div>
);
